import { useState } from 'react';
import { CSVLink } from 'react-csv';
import { useSearchParams } from 'react-router-dom';
import { NodeAccessApproval, NodeAccessOperation, NodeAccessProfileStatus } from '@prisma/client';
import { Breadcrumb, Button, Input, Layout, Modal, Select, Space, Table } from 'antd';

import { ApprovalStage } from '@app/domain-t3-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { PageLayout, Tables } from '@app/web-components';

import { NodeSupplierContracts } from '../../admin';
import { ProfileOperationRenderer, ProfileStatusRenderer } from '../../profiles';
import { useFindApprovals, useFindApprovalsByContract } from '../data';
import { useApprovalActions } from '../libs';

export type LandingPageItem = NodeAccessApproval;

export const LandingPage = () => {
  const { getApprovalModalProps, getActionButton } = useApprovalActions();
  const [searchParams, setSearchParams] = useSearchParams({});
  const [selectedContract, setSelectedContract] = useState<string | undefined>(searchParams.get('contractNumber'));

  const contractApprovals = useFindApprovalsByContract(selectedContract ?? '', { enabled: !!selectedContract });

  const approvalStages = {
    [ApprovalStage.MANAGER]: 'CW Sponsor',
    [ApprovalStage.EMS_ACCOUNT_MANAGER]: 'EMS Account Manager',
    [ApprovalStage.EXTERNAL_TSMC]: 'External TSMC',
  };

  const { getSelectProps: getContractSelectProps } = NodeSupplierContracts.useContractPicker({
    labelInValue: false,
    value: selectedContract,
  });

  const {
    getParentLayoutContentProps,
    getSearchInputProps,
    getReloadButtonProps,
    getCSVLinkProps,
    getResetFiltersButtonProps,
    getTableProps,
    getToolbarProps,
    getQuery,
  } = Tables.useTableState<LandingPageItem>(
    'Approvals',
    [
      {
        title: 'Action',
        width: 120,
        render: (_, record) => getActionButton(record),
      },
      {
        title: 'Operation',
        dataIndex: 'operation',
        width: 120,
        render: (operation) => <ProfileOperationRenderer operation={operation} />,
        filters: [NodeAccessOperation.ADD, NodeAccessOperation.DELETE].map((status) => ({
          text: status,
          value: status,
        })),
        filterSearch: true,
        sorter: true,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 120,
        render: (status) => <ProfileStatusRenderer status={status} />,
        filters: [
          NodeAccessProfileStatus.PENDING,
          NodeAccessProfileStatus.APPROVED,
          NodeAccessProfileStatus.REJECTED,
        ].map((status) => ({
          text: status,
          value: status,
        })),
        filterSearch: true,
        sorter: true,
        defaultSortOrder: 'ascend',
      },
      {
        title: 'Approval Stage',
        dataIndex: 'approvalStageName',
        width: 150,
        filters: Object.entries(approvalStages).map(([key, value]) => ({
          text: value,
          value: key,
        })),
        render: (stage) => approvalStages[stage],
        filterSearch: true,
        ellipsis: true,
        sorter: true,
      },
      {
        title: 'CW Name',
        dataIndex: 'name',
        width: 200,
        sorter: true,
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search users'),
      },
      {
        title: 'CW Job Title',
        dataIndex: 'jobRole',
        width: 150,
        sorter: true,
        ellipsis: true,
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search job roles'),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        width: 150,
        sorter: true,
        ellipsis: true,
      },
      {
        title: 'Project',
        dataIndex: 'dieName',
        width: 150,
        sorter: true,
        ellipsis: true,
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search projects'),
      },
      {
        title: 'Node',
        dataIndex: 'nodeName',
        width: 120,
        sorter: true,
        // Filterdropdown instead of filters here because this is an external page and users don't need to know all the nodes present.
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search nodes'),
      },
      {
        title: 'Contract Name',
        dataIndex: 'contractName',
        width: 200,
        sorter: true,
        ellipsis: true,
      },
      {
        title: 'Contract Number',
        dataIndex: 'contractNumber',
        width: 200,
        sorter: true,
      },
      {
        title: 'Intel Signed Date',
        dataIndex: 'intelSignedDate',
        width: 150,
        ellipsis: true,
        sorter: true,
        render: (date) => formatIsoDate(date),
      },
    ],
    {
      globalSearchFields: ['name', 'dieName', 'nodeName', 'contractName'],
    },
  );

  const queryResult = useFindApprovals<LandingPageItem>(
    getQuery({
      where: {
        contractNumber: selectedContract ?? undefined,
      },
    }),
    { refetchInterval: 60000 },
  );

  return (
    <>
      <Modal {...getApprovalModalProps()} />
      <Breadcrumb style={{ margin: '16px 0' }} items={[{ title: 'Home' }, { title: 'Approvals' }]} />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Approvals" />
        <Space direction="vertical">
          <Select
            {...getContractSelectProps({
              allowClear: true,
              placeholder: 'Search Contracts',
              onChange: (newValue) => {
                setSelectedContract(newValue);
                setSearchParams({ contractNumber: newValue });
              },
            })}
            value={selectedContract}
          />
          <div
            {...getToolbarProps({
              items: [
                <Input {...getSearchInputProps()} />,
                <Button
                  {...getReloadButtonProps()}
                  onClick={() => {
                    queryResult?.refetch();
                    if (selectedContract) contractApprovals?.refetch();
                  }}
                />,
                <Button {...getResetFiltersButtonProps()} />,
                <CSVLink {...getCSVLinkProps(queryResult?.data?.data)} />,
              ],
              queryResult,
            })}
          />
        </Space>
        <Table {...getTableProps(queryResult)} rowKey={(record) => record.id} />
      </Layout.Content>
    </>
  );
};
